/**
 * Where the tokens live.
 *
 * The access token is held in MEMORY only, so it is never readable from
 * storage by injected script and disappears when the tab closes.
 *
 * The refresh token is in localStorage, which is the honest tradeoff in this
 * MVP: without a backend session cookie there is nowhere better, and losing
 * the session on every page reload would be unusable. It is mitigated on the
 * server side — refresh tokens are single-use, rotated on every exchange, and
 * presenting a rotated one revokes the entire session family. See
 * iam-service/src/services/authService.js.
 *
 * The upgrade path is an httpOnly, SameSite=Strict cookie issued by the
 * gateway; nothing outside this file would need to change.
 */

const REFRESH_KEY = 'erp.refreshToken';
const TENANT_KEY = 'erp.tenantId';

let accessToken = null;
const listeners = new Set();

const notify = () => {
  for (const listener of listeners) listener();
};

/** Lets React subscribe to sign-in and sign-out. */
export function subscribe(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export const getAccessToken = () => accessToken;

export function setTokens({ accessToken: access, refreshToken } = {}) {
  accessToken = access ?? null;
  if (refreshToken) {
    try {
      localStorage.setItem(REFRESH_KEY, refreshToken);
    } catch {
      // Private browsing with storage disabled: the session simply will not
      // survive a reload, which is better than failing the sign-in.
    }
  }
  notify();
}

export function getRefreshToken() {
  try {
    return localStorage.getItem(REFRESH_KEY);
  } catch {
    return null;
  }
}

export function clearTokens() {
  accessToken = null;
  try {
    localStorage.removeItem(REFRESH_KEY);
  } catch {
    /* ignored */
  }
  notify();
}

// --- selected organization -------------------------------------------------
// Persisted so a reload keeps you in the same organization. It is a UI
// preference, not a permission: every request revalidates it server-side.

export function getTenantId() {
  try {
    return localStorage.getItem(TENANT_KEY);
  } catch {
    return null;
  }
}

export function setTenantId(tenantId) {
  try {
    if (tenantId) localStorage.setItem(TENANT_KEY, tenantId);
    else localStorage.removeItem(TENANT_KEY);
  } catch {
    /* ignored */
  }
}

/** Decodes a JWT payload for display only. NEVER for an access decision. */
export function readTokenPayload(token = accessToken) {
  if (!token) return null;
  try {
    const [, payload] = token.split('.');
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch {
    return null;
  }
}
